import { FunctionComponent, useEffect, useState } from 'react';
import { subWeeks, format } from 'date-fns';
import { Formik } from 'formik';

import * as vp from 'vopay';
import {
  Box,
  Select,
  TextField,
  DateRangePicker,
  TransactionHistoryStatus,
  DatepickerSimple,
  convertToOptions,
} from 'vopay';
import { useTranslation } from 'react-i18next';
import { Button } from 'UI';
import * as Yup from 'yup';
import styles from './TransactionsSearchForm.module.scss';

type SearchTransactionsProps = {
  hasInvalidEndDate?: boolean;
  shouldShowEndDateError?: boolean;
  handlers: {
    onTransactionsSearch: (values: vp.AccountTransactionsRequest) => void;
    handleDate: (values: [vp.Timestamp, vp.Timestamp] | undefined) => void;
  };
  setShouldShowEndDateError?: (event: boolean) => void;
  search: (values: vp.AccountTransactionsRequest) => void;
  timeRange: [vp.Timestamp, vp.Timestamp] | undefined;
  setTimeRange: (event: [vp.Timestamp, vp.Timestamp] | undefined) => void;
  defaultValues: vp.AccountTransactionsRequest;
};

const TransactionsSearchForm: FunctionComponent<SearchTransactionsProps> = ({
  handlers: formHandlers,
  hasInvalidEndDate,
  shouldShowEndDateError,
  setShouldShowEndDateError,
  search,
  timeRange,
  setTimeRange,
  defaultValues,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Transaction.Table' });
  const initialTimeRange: [vp.Timestamp, vp.Timestamp] = [
    subWeeks(new Date(), 1).getTime() as vp.Timestamp,
    new Date().getTime() as vp.Timestamp,
  ];

  useEffect(() => {
    formHandlers.handleDate(timeRange);
  }, [timeRange]);

  const currencyOptions = [...Object.entries(vp.CURRENCY_SELECT_OPTIONS).map(([label, value]) => ({ label, value }))];

  const transactionOptions = [
    ...Object.entries(vp.TRANSACTION_TYPE_SELECT_OPTIONS).map(([label, value]) => ({ label, value })),
  ];

  const transactionsFormSchema = Yup.object().shape({
    StartDateTime: Yup.string().required('Required'),
    EndDateTime: Yup.string().required('Required'),
  });

  const [currentFlagged, setCurrentFlagged] = useState<string>('');
  const [currentSettlementDate, setCurrentSettlementDate] = useState<vp.Timestamp | undefined>(undefined);

  const statusOptions: Record<Exclude<TransactionHistoryStatus, null>, string> = {
    All: t('all', { defaultValue: 'All' }),
    requested: t('requested', { defaultValue: 'Requested' }),
    pending: t('pending', { defaultValue: 'Pending' }),
    'in progress': t('inProgress', { defaultValue: 'In Progress' }),
    sent: t('sent', { defaultValue: 'Sent' }),
    successful: t('successful', { defaultValue: 'Successful' }),
    complete: t('complete', { defaultValue: 'Complete' }),
    settled: t('settled', { defaultValue: 'Settled' }),
    failed: t('failed', { defaultValue: 'Failed' }),
    cancelled: t('cancelled', { defaultValue: 'Cancelled' }),
    'cancellation requested': t('cancellationRequested', { defaultValue: 'Cancellation Requested' }),
    'pending approval': t('pendingApproval', { defaultValue: 'Pending Approval' }),
    waiting: t('waiting', { defaultValue: 'Waiting' }),
  };

  const flaggedOptions = {
    All: t('all', { defaultValue: 'All' }),
    Yes: t('yes', { defaultValue: 'Yes' }),
    No: t('no', { defaultValue: 'No' }),
  };

  return (
    <Box ai="center">
      <Formik
        initialValues={defaultValues}
        validationSchema={transactionsFormSchema}
        validateOnChange
        validateOnBlur
        onSubmit={search}
      >
        {({ values, handleChange, handleBlur, handleSubmit, setFieldValue }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DateRangePicker
                onChange={setTimeRange}
                initialTime={timeRange || initialTimeRange}
                hasInvalidEndDate={hasInvalidEndDate}
                shouldShowEndDateError={shouldShowEndDateError}
                setShouldShowEndDateError={setShouldShowEndDateError}
                triggerVariant="outlineShadow"
              />

              <div className={styles.row}>
                <Select
                  version="2"
                  placeholder={t('currency', { defaultValue: 'Currency' })}
                  options={currencyOptions}
                  name="Currency"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.Currency || ''}
                  sort={false}
                />

                <div className={styles.type}>
                  <Select
                    version="2"
                    placeholder={t('type', { defaultValue: 'Type' })}
                    options={transactionOptions}
                    name="TransactionType"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.TransactionType || ''}
                    sort={false}
                  />
                </div>

                <div className={styles.status}>
                  <Select
                    version="2"
                    placeholder={t('status', { defaultValue: 'Status' })}
                    options={convertToOptions(statusOptions)}
                    name="TransactionStatus"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.TransactionStatus}
                    sort={false}
                  />
                </div>

                <Select
                  version="2"
                  placeholder={t('flagged', { defaultValue: 'Flagged' })}
                  options={convertToOptions(flaggedOptions)}
                  name="IsFlagged"
                  value={currentFlagged}
                  onBlur={handleBlur}
                  onChange={(e) => {
                    const selected = e.target.value;
                    const flaggedValue = selected === 'No' ? false : selected === 'Yes' ? true : undefined;

                    setFieldValue('IsFlagged', flaggedValue);
                    setCurrentFlagged(selected);
                  }}
                  sort={false}
                />

                <div className={styles.settlementDate}>
                  <DatepickerSimple
                    variant="outlineShadow"
                    value={(currentSettlementDate || '').toString()}
                    selected={currentSettlementDate}
                    format="timestamp"
                    position="bottomLeft"
                    onChange={(e) => {
                      const selected = JSON.parse(e.target.value);
                      const formattedSettlementDate = format(new Date(selected), 'yyyy-MM-dd');

                      setFieldValue('SettlementDate', formattedSettlementDate);
                      setCurrentSettlementDate(e.target.value ? selected : undefined);
                    }}
                    onBlur={handleBlur}
                    name="SettlementDate"
                    placeholder={t('settlementDate', { defaultValue: 'Settlement Date' })}
                    inputWidth="auto"
                    className={styles.settlementDatePicker}
                    allowBlank
                  />
                </div>

                <TextField
                  key="TransactionSearchKeyword"
                  name="SearchText"
                  placeholder={t('keyword', { defaultValue: 'Keyword' })}
                  value={values.SearchText}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  variant="outlineShadow"
                />

                <Button id="account-transactions-search" type="submit" colorScheme="primary" size="small">
                  {t('searchButton', { defaultValue: 'Search' })}
                </Button>
              </div>
            </form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default TransactionsSearchForm;
